import React, { useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha"; // 1. Import ReCAPTCHA
import { CustomInput, CustomSelect } from "../../../../components";

export default function DomainExpertiseForm({
  initialData = {},
  onFieldChange,
  onBack,
  onContinue,
}) {
  // ------------------ Domain Options ------------------
  const domainOptions = [
    { value: "Sales and Marketing", label: "Sales and Marketing" },
    {
      value: "IT Helpdesk and Customer Support Services",
      label: "IT Helpdesk and Customer Support Services",
    },
    { value: "Finance and Accounting", label: "Finance and Accounting" },
    {
      value: "Human Resource Management",
      label: "Human Resource Management",
    },
    {
      value: "Business Email and Collaboration",
      label: "Business Email and Collaboration",
    },
    {
      value: "Custom Solutions and Platform Development",
      label: "Custom Solutions and Platform Development",
    },
    { value: "Security and IT Management", label: "Security and IT Management" },
    { value: "Legal", label: "Legal" },
  ];

  // ------------------ Hear About Options ------------------
  const hearAboutOptions = [
    "Advertisements",
    "Community / Forums",
    "Events / Tradeshows",
    "Social Media",
    "Through a Friend",
    "Docusafe Invite",
    "Docusafe Customer",
    "Docusafe Website",
    "Web Search",
    "Other",
  ].map((x) => ({ value: x, label: x }));

  const experienceDropdown = [
    { value: "1_year_or_less", label: "1 year or less" },
    { value: "2_to_5_years", label: "2–5 years" },
    { value: "5_plus_years", label: "5+ years" },
  ];

  // ------------------ Domain Questions ------------------
  const domainConfig = {
    "Sales and Marketing": [
      { name: "crm_projects", label: "CRM implementation projects completed", type: "input" },
      { name: "team_size", label: "Size of your CRM / sales team", type: "input" },
      { name: "experience_years", label: "Years of experience in Sales & Marketing", type: "select", options: experienceDropdown },
      { name: "avg_deal_size", label: "Average deal size (in your currency)", type: "input" },
    ],
    "IT Helpdesk and Customer Support Services": [
      { name: "support_projects", label: "IT helpdesk / support projects completed", type: "input" },
      { name: "team_size", label: "Support team size", type: "input" },
      { name: "monthly_tickets", label: "Average monthly tickets handled", type: "input" },
      { name: "experience_years", label: "Years of experience in support domain", type: "select", options: experienceDropdown },
    ],
    "Finance and Accounting": [
      { name: "finance_projects", label: "Finance / accounting projects completed", type: "input" },
      { name: "team_size", label: "Finance team size", type: "input" },
      { name: "experience_years", label: "Years of finance & accounting experience", type: "select", options: experienceDropdown },
    ],
    "Human Resource Management": [
      { name: "hr_projects", label: "HRMS / HR projects completed", type: "input" },
      { name: "team_size", label: "HR consulting team size", type: "input" },
      { name: "experience_years", label: "Years of HR domain experience", type: "select", options: experienceDropdown },
    ],
    "Business Email and Collaboration": [
      { name: "migration_projects", label: "Email migration / collaboration projects completed", type: "input" },
      { name: "team_size", label: "IT collaboration / infra team size", type: "input" },
      { name: "experience_years", label: "Years of experience in email & collaboration", type: "select", options: experienceDropdown },
    ],
    "Custom Solutions and Platform Development": [
      { name: "apps_built", label: "Custom business applications developed", type: "input" },
      { name: "team_size", label: "Developer / product team size", type: "input" },
      { name: "experience_years", label: "Years of software development experience", type: "select", options: experienceDropdown },
    ],
    "Security and IT Management": [
      { name: "security_projects", label: "Security / IAM / IT management projects completed", type: "input" },
      { name: "team_size", label: "Security / IT operations team size", type: "input" },
      { name: "experience_years", label: "Years of experience in security / IT management", type: "select", options: experienceDropdown },
    ],
    Legal: [
      { name: "clm_projects", label: "CLM / legal tech projects completed", type: "input" },
      { name: "team_size", label: "Legal / CLM team size", type: "input" },
      { name: "experience_years", label: "Years of experience in legal / contracts domain", type: "select", options: experienceDropdown },
    ],
  };

  const [selectedDomain, setSelectedDomain] = useState(initialData.domain || "");
  const domainFields = domainConfig[selectedDomain] || [];

  // ------------------ FORM STATE ------------------
  const [form, setForm] = useState({
    domain: initialData.domain || "",
    heard_about: initialData.heard_about || "",
    contact_notes: initialData.contact_notes || "",
    captcha: "", // Isme ab ReCAPTCHA ka token store hoga
    custom_fields: initialData.custom_fields || {},
  });

  const [errors, setErrors] = useState({});

  // OLD CAPTCHA LOGIC REMOVED (Random text generation hataya)

  useEffect(() => {
    onFieldChange && onFieldChange(form);
  }, [form]);

  // ------------------ Handlers ------------------
  const handleDomainChange = (e) => {
    const value = e.target.value;
    setSelectedDomain(value);
    setForm((prev) => ({
      ...prev,
      domain: value,
      custom_fields: {},
    }));
    setErrors((prev) => ({ ...prev, domain: "" }));
  };

  const handleCustomFieldChange = (e, name) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      custom_fields: { ...prev.custom_fields, [name]: value },
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // 2. New Handler for Google Recaptcha
  const onCaptchaChange = (value) => {
    // Value null hoga agar user expire ho gaya ya check fail hua
    setForm((prev) => ({ ...prev, captcha: value }));
    if (value) {
      setErrors((prev) => ({ ...prev, captcha: "" }));
    }
  };

  const handleSubmit = () => {
    let validation = {};

    if (!form.domain) validation.domain = "Required";

    domainFields.forEach((field) => {
      const val = form.custom_fields[field.name];
      if (!val || val.trim() === "") validation[field.name] = "Required";
    });

    if (!form.heard_about) validation.heard_about = "Required";

    // 3. Updated Validation logic
    if (!form.captcha) {
        validation.captcha = "Please verify you are not a robot";
    }

    setErrors(validation);

    if (Object.keys(validation).length === 0) {
      onContinue && onContinue(form);
    }
  };

  return (
    <div className="partner-step-wrapper">
      <h2 className="apply-title">Domain & Program Details</h2>

      {/* DOMAIN */}
      <div className="row">
        <div className="col-md-6 col-12 py-2">
          <CustomSelect
            label="Domain Expertise"
            name="domain"
            required
            options={domainOptions}
            value={form.domain}
            onChange={handleDomainChange}
            error={errors.domain}
          />
        </div>
      </div>

      {/* DOMAIN QUESTIONS */}
      {selectedDomain && (
        <div className="row">
          {domainFields.map((field, index) => (
            <div className="col-md-6 col-12 py-2" key={index}>
              {field.type === "select" ? (
                <CustomSelect
                  label={field.label}
                  name={field.name}
                  required
                  options={field.options}
                  value={form.custom_fields[field.name] || ""}
                  error={errors[field.name]}
                  onChange={(e) => handleCustomFieldChange(e, field.name)}
                />
              ) : field.type === "textarea" ? (
                <>
                  <label className="custom-select-label">
                    {field.label} <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      errors[field.name] ? "is-invalid" : ""
                    }`}
                    rows={4}
                    value={form.custom_fields[field.name] || ""}
                    onChange={(e) => handleCustomFieldChange(e, field.name)}
                  />
                  {errors[field.name] && (
                    <div className="invalid-feedback d-block">
                      {errors[field.name]}
                    </div>
                  )}
                </>
              ) : (
                <CustomInput
                  label={field.label}
                  required
                  value={form.custom_fields[field.name] || ""}
                  error={errors[field.name]}
                  onChange={(e) => handleCustomFieldChange(e, field.name)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* CONTACT SECTION */}
      {selectedDomain && (
        <div className="row">
          <div className="col-md-6 col-12 py-2">
            <CustomSelect
              label="How did you hear about Docusafe?"
              name="heard_about"
              required
              options={hearAboutOptions}
              value={form.heard_about}
              error={errors.heard_about}
              onChange={(e) =>
                setForm((p) => ({ ...p, heard_about: e.target.value }))
              }
            />
          </div>

          <div className="col-md-12 col-12 py-2">
            <label className="custom-select-label">Additional Notes (optional)</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Any additional details..."
              value={form.contact_notes}
              onChange={(e) =>
                setForm((p) => ({ ...p, contact_notes: e.target.value }))
              }
            />
          </div>
        </div>
      )}

      {/* 4. NEW CAPTCHA UI */}
      {selectedDomain && (
        <div className="row">
          <div className="col-12 py-2">
            <label className="custom-select-label mb-2">Security Check</label>
            <ReCAPTCHA
                sitekey="6LfajNMqAAAAAKnoorhq_go5iAlhDkCaI4HXyLpo" 
                onChange={onCaptchaChange}
            />
            {errors.captcha && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                    {errors.captcha}
                </div>
            )}
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div className="fixed-footer-actions">
        <button type="button" className="partner-btn-outline" onClick={onBack}>
          Back
        </button>

        <button type="button" className="partner-btn-primary" onClick={handleSubmit}>
          Submit
        </button>
      </div>
    </div>
  );
}